/**
 * Level 19: Prismatic Skyway
 * 
 * A breathtaking sky palace above the clouds with rainbow-hued
 * crystal platforms. Combines all three new enemy types in a
 * balanced, aerial-focused challenge.
 * 
 * Design philosophy: Vertical + horizontal hybrid layout.
 * Low gravity with wind currents creates a floaty, musical feel.
 * Each section introduces enemy combinations progressively.
 * 
 * @module configs/levels/Level19_PrismaticSkyway
 */

/** @type {import('../LevelTypes').LevelConfig} */
const Level19_PrismaticSkyway = {
    id: 19,
    name: 'Prismatic Skyway',
    subtitle: 'Above the Clouds',
    description: 'Soar through a rainbow palace in the sky with every challenge combined.',

    worldWidth: 5600,
    worldHeight: 2400,
    playerStart: { x: 150, y: 2100 },
    gravity: 0.7,

    theme: {
        bgGradient: ['#e0f7fa', '#7c4dff'],
        platformColor: 0x7e57c2,
        accentColor: 0xffab40,
        particleColor: 0xff80ab,
        ambient: 'sky'
    },

    platforms: [
        // Cloud base - gentle introduction
        { x: 0, y: 2200, w: 400, h: 55 },
        { x: 500, y: 2050, w: 180, h: 25 },
        { x: 750, y: 1900, w: 200, h: 28 },

        // Rainbow bridge section
        { x: 1000, y: 1750, w: 280, h: 35 },
        { x: 1350, y: 1600, w: 200, h: 28 },
        { x: 1600, y: 1450, w: 250, h: 30 },

        // Crystal garden
        { x: 1900, y: 1600, w: 200, h: 28 },
        { x: 2150, y: 1450, w: 250, h: 35 },
        { x: 2450, y: 1300, w: 180, h: 25 },

        // Wind corridor
        { x: 2700, y: 1450, w: 300, h: 40 },
        { x: 3050, y: 1300, w: 200, h: 28 },
        { x: 3300, y: 1150, w: 250, h: 30 },

        // Prism tower ascent
        { x: 3600, y: 1350, w: 200, h: 28 },
        { x: 3500, y: 1150, w: 160, h: 22 },
        { x: 3700, y: 950, w: 180, h: 25 },
        { x: 3550, y: 750, w: 200, h: 28 },

        // Skyway finale
        { x: 3850, y: 900, w: 300, h: 40 },
        { x: 4200, y: 750, w: 200, h: 28 },
        { x: 4450, y: 600, w: 250, h: 30 },
        { x: 4750, y: 800, w: 200, h: 28 },
        { x: 5000, y: 650, w: 300, h: 45 },
        { x: 5300, y: 850, w: 300, h: 50 }
    ],

    movingPlatforms: [
        // Rainbow bridges
        { x: 450, y: 2120, w: 110, h: 20, moveY: 120, speed: 0.002 },
        { x: 1250, y: 1660, w: 120, h: 20, moveX: 100, speed: 0.0025 },
        // Crystal lifts
        { x: 2600, y: 1370, w: 100, h: 18, moveY: 140, speed: 0.003 },
        // Wind-assisted  
        { x: 3150, y: 1230, w: 110, h: 20, moveX: 120, speed: 0.0025 },
        // Prism ascenders
        { x: 3650, y: 1050, w: 100, h: 18, moveY: 150, speed: 0.002 },
        // Finale approach
        { x: 4600, y: 690, w: 120, h: 20, moveX: 130, speed: 0.003 }
    ],

    spikes: [
        { x: 1050, y: 1750 },
        { x: 1100, y: 1750 },
        { x: 2200, y: 1450 },
        { x: 2750, y: 1450 },
        { x: 2800, y: 1450 },
        { x: 3650, y: 1350 },
        { x: 4500, y: 600 },
        { x: 4550, y: 600 },
        { x: 5050, y: 650 },
        { x: 5350, y: 850 }
    ],

    stars: [
        { x: 750, y: 1800 },
        { x: 1600, y: 1350 },
        { x: 2450, y: 1200 },
        { x: 3300, y: 1050 },
        { x: 3700, y: 650 },
        { x: 4450, y: 500 },
        { x: 5200, y: 750 }
    ],

    enemies: [
        // Flying enemies - natural for sky theme
        { x: 600, y: 1800, type: 'flying', options: { amplitude: 70, speed: 1.2, walkDist: 130, color: 0xff66cc } },
        { x: 2000, y: 1300, type: 'flying', options: { amplitude: 60, speed: 1.5, walkDist: 120, color: 0x66ccff } },
        { x: 4300, y: 550, type: 'flying', options: { amplitude: 80, speed: 1.3, walkDist: 140, color: 0xffcc66 } },
        // Shield enemies guarding key passages
        { x: 1100, y: 1700, type: 'shield', options: { speed: 1.2, walkDist: 90, slamInterval: 2500, shieldColor: 0xff66cc } },
        { x: 3050, y: 1250, type: 'shield', options: { speed: 1.0, walkDist: 70, slamInterval: 3000, shieldColor: 0x66ccff } },
        // Exploding enemies near stars  
        { x: 2500, y: 1250, type: 'exploding', options: { detectionRange: 120, fuseDuration: 1500, speed: 0.8, walkDist: 60 } },
        { x: 4800, y: 750, type: 'exploding', options: { detectionRange: 130, fuseDuration: 1400, speed: 0.9, walkDist: 70 } },
        // Regular enemies  
        { x: 1650, y: 1400 },
        { x: 3850, y: 850 }
    ],

    door: { x: 5450, y: 700, w: 55, h: 300 },
    button: { x: 5300, y: 845 },
    box: { x: 5050, y: 550, floating: true },
    goal: { x: 5500, y: 750 },

    windZones: [
        // Upward thermal currents
        { x: 2650, y: 1100, w: 300, h: 500, forceX: 0, forceY: -2.0 },
        // Cross breeze
        { x: 3800, y: 600, w: 400, h: 500, forceX: 1.5, forceY: -1.0 },
        // Gentle updraft at end
        { x: 5100, y: 500, w: 300, h: 500, forceX: 0, forceY: -1.5 }
    ],

    portals: [
        // Cloud portal shortcut
        { x1: 350, y1: 2150, x2: 1900, y2: 1550, color: 0xffab40 },
        // Prism warp
        { x1: 2400, y1: 1250, x2: 4150, y2: 700, color: 0xff80ab }
    ],

    trampolines: [
        { x: 950, y: 1750, w: 70 },
        { x: 1550, y: 1450, w: 70 },
        { x: 3550, y: 1350, w: 70 },
        { x: 4700, y: 800, w: 70 }
    ],

    speedrunTargets: { gold: 75, silver: 100, bronze: 130 }
};

export default Level19_PrismaticSkyway;
